import type { CaptureOptions, Capturer } from './dom-capture'

/** Marks the live 3D canvas so the capture can substitute its snapshot. */
export const CANVAS_3D_ATTRIBUTE = 'data-bundle-3d'

/**
 * Captures the app with `modern-screenshot`, which serialises the DOM into an
 * SVG `<foreignObject>` and rasterises that.
 *
 * WHAT IS ACTUALLY KNOWN, and no more. Two earlier versions of this comment
 * asserted a root cause; both were wrong, and each was more plausible than the
 * truth. So this one states the evidence and stops.
 *
 * Observed: html2canvas produced unstyled captures — controls stacked against
 * the left edge, as though no CSS had loaded.
 *
 * Ruled out: "html2canvas has no CSS Grid engine, so grid collapses to block
 * flow". It implements no layout at all and does not need to. It clones the
 * document into an attached iframe, reads positions with
 * `getBoundingClientRect` and appearance with `getComputedStyle`, then paints
 * what the browser measured.
 *
 * NOT established: why the capture was unstyled. "The clone never received the
 * stylesheets" fits the symptom, but html2canvas preserves stylesheet links and
 * serialises `<style>` rules when cloning, so it is a weaker explanation than
 * it first appears. Determining the real cause needs a browser, and this change
 * does not depend on knowing it.
 *
 * Why this is expected to help anyway: `modern-screenshot` reads each node's
 * computed style and writes the differences inline on the clone. It does
 * reconstruct appearance from computed styles — the earlier claim here that
 * "nothing is reconstructed" was also wrong — but doing so removes the
 * dependency on stylesheets resolving inside the serialised document, which is
 * the failure mode the symptom points at.
 *
 * VERIFIED 2026-09-07 by Michael Fork: captures from the deployed build render
 * correctly. The replacement works.
 *
 * The cause of the html2canvas failure is still not known and now probably
 * never will be — the evidence went with the library. Recorded so the next
 * reader does not mistake "the fix works" for "we understood it", and does not
 * reintroduce html2canvas here expecting a different result.
 */

/**
 * Detects a capture that came back empty.
 *
 * `modern-screenshot` does not throw when the render fails: it resolves on
 * image-load errors and timeouts, and catches `drawImage` failures and returns
 * the canvas regardless. So a failed rasterisation arrives as a fully
 * transparent PNG — and without this check the pipeline labels it `included`,
 * sending triage after a rendering bug that does not exist while the real
 * failure goes unrecorded.
 *
 * Samples rather than scanning: at a full viewport this runs over millions of
 * pixels, and any single opaque pixel is enough to prove something was painted.
 * The stride is prime so it cannot fall into step with a repeating pattern.
 */
export function isBlank(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext('2d')
  if (!context) return false

  let data: Uint8ClampedArray
  try {
    data = context.getImageData(0, 0, canvas.width, canvas.height).data
  } catch {
    // Tainted. Unreadable is not the same as empty, so do not call it blank.
    return false
  }

  // 4 bytes per pixel; step over whole pixels, checking only alpha.
  for (let i = 3; i < data.length; i += 4 * 997) {
    if ((data[i] ?? 0) !== 0) return false
  }
  return true
}

interface Installed {
  /** How many captures are currently relying on this override. */
  users: number
  /** What `toDataURL` was before the first of them installed it. */
  original: PropertyDescriptor | undefined
}

/**
 * Overrides currently installed, counted.
 *
 * A count rather than a flag, because the flag solved the wrong half of the
 * problem. It stopped a second capture reinstalling the first one's override
 * permanently — but it also let the FIRST capture restore while the second was
 * still cloning, and `modern-screenshot` reads `toDataURL` lazily, when its
 * traversal reaches that canvas. The second capture would then read the live
 * WebGL canvas, whose buffer is cleared by compositing time, and ship a blank
 * viewport. Quieter than the freeze it replaced.
 *
 * Counting keeps the override alive until the last capture using it is done.
 *
 * A WeakMap so a removed canvas is not kept alive by the bookkeeping that
 * exists only to protect it.
 */
const INSTALLED = new WeakMap<HTMLCanvasElement, Installed>()

/**
 * Makes the 3D viewport read as its on-demand snapshot.
 *
 * A WebGL drawing buffer is cleared by compositing time, so `toDataURL` on the
 * live 3D canvas returns blank. The 3D view renders a snapshot on demand — but
 * the library converts every canvas to an image DURING cloning, before any hook
 * we could use runs, and the image it creates carries none of the original's
 * attributes, so the substitution cannot be made afterwards either.
 *
 * So it is made BEFORE. Overriding `toDataURL` on the one live element means
 * the library's own canvas path produces the right image with no special case:
 * we supply better data rather than fighting the pipeline.
 *
 * Returns a restore function. The override MUST NOT outlive the capture — it is
 * a live element the app keeps using.
 */
/**
 * Makes a release callable more than once without counting more than once.
 *
 * The count is shared state on a live element. A caller that released twice —
 * a stray retry, a `finally` that runs after an explicit call — would drive it
 * negative, and the next capture would then join at zero and restore the
 * override while still using it. Cheaper to make the callback one-shot than to
 * require every caller to be careful.
 */
function once(fn: () => void): () => void {
  let spent = false
  return (): void => {
    if (spent) return
    spent = true
    fn()
  }
}

/**
 * Drops one user of an override, restoring the original when the last leaves.
 *
 * The bookkeeping is cleared in a `finally` so a throw from `defineProperty`
 * cannot leave the entry latched — that would make every later capture join a
 * phantom override and read a snapshot nobody is maintaining.
 */
function release(canvas: HTMLCanvasElement): void {
  const entry = INSTALLED.get(canvas)
  if (!entry) return

  entry.users -= 1
  if (entry.users > 0) return

  try {
    if (entry.original) Object.defineProperty(canvas, 'toDataURL', entry.original)
    else Reflect.deleteProperty(canvas, 'toDataURL')
  } finally {
    INSTALLED.delete(canvas)
  }
}

export function overrideSnapshot(
  element: HTMLElement,
  snapshot3D?: string | null,
): () => void {
  if (!snapshot3D) return (): void => {}

  const canvas = element.querySelector<HTMLCanvasElement>(`canvas[${CANVAS_3D_ATTRIBUTE}]`)
  if (!canvas) return (): void => {}

  // Join an existing override rather than stacking on it, and keep the FIRST
  // snapshot. A second caller's frame is silently ignored, which is right here
  // because both come from the same 3D view moments apart — but it is a policy
  // rather than an accident, so it is stated and pinned by test. Swapping the
  // frame mid-capture would be worse: `modern-screenshot` reads lazily, so the
  // two captures could serialise different frames of the same viewport.
  const existing = INSTALLED.get(canvas)
  if (existing) {
    existing.users += 1
    return once((): void => release(canvas))
  }

  // Capture whatever is there first. Deleting the own property afterwards
  // would be right only if nothing had one — and if something did (a test
  // double, an instrumentation shim), deleting it silently destroys it and the
  // canvas starts answering from the prototype instead. Restoring the exact
  // descriptor is correct in both cases.
  const original = Object.getOwnPropertyDescriptor(canvas, 'toDataURL')

  // Recorded only once the mutation has actually happened. Registering first
  // and then throwing — a non-extensible canvas, an incompatible descriptor —
  // would latch the entry with no restore ever issued, and the 3D snapshot
  // would be silently dead for the rest of the session.
  Object.defineProperty(canvas, 'toDataURL', {
    value: (): string => snapshot3D,
    configurable: true,
  })
  INSTALLED.set(canvas, { users: 1, original })

  return once((): void => release(canvas))
}

export const foreignObjectCapturer: Capturer = async (
  element: HTMLElement,
  options?: CaptureOptions,
): Promise<HTMLCanvasElement> => {
  // Dynamic so it stays out of the eager bundle: most sessions never trigger
  // this feature.
  const { domToCanvas } = await import('modern-screenshot')

  const restore = overrideSnapshot(element, options?.snapshot3D)
  let canvas: HTMLCanvasElement
  try {
    canvas = await domToCanvas(element, {
      // Full device pixel ratio would multiply the byte cost of the artifact
      // that is already first to be dropped when a bundle is too large.
      scale: 1,
      // Off by default in the library, and wrong for us. Without it every
      // scrolled container clones at position zero — the virtualised results
      // table would arrive showing rows the user was not looking at, or none
      // at all. Nested under `features`, not top level; the typecheck is the
      // only thing that says so.
      features: { restoreScrollPosition: true },
    })
  } finally {
    restore()
  }

  // Deliberately throws rather than returning a transparent PNG.
  //
  // What follows is worth knowing exactly, because "it throws, so it reports
  // failed" is what the previous comment claimed and it is not true:
  // `captureViewport` turns this into `undefined`, and the container then
  // substitutes the stored Konva snapshot if one exists — which the controller
  // reports as `included`. So a failed viewport capture can still arrive as a
  // screenshot, of the whole diagram at a normalised zoom rather than of what
  // the user was looking at.
  //
  // Note the fallback is not guaranteed: `schematicSnapshot` is null until the
  // 2D view has been opened, so a session that never left the 3D view gets
  // `failed` rather than a substitute. Both beat a transparent PNG.
  //
  // That fallback is deliberate and predates this change. What it does not do
  // is say which of the two triage is looking at, and the wire enum has no
  // value for "fallback" — adding one is a contract change with the receiver.
  // Tracked separately rather than smuggled into a rendering fix.
  //
  // Either way a blank PNG labelled `included` is strictly worse than both, and
  // that is what this prevents.
  if (isBlank(canvas)) throw new Error('screenshot rendered empty')

  return canvas
}
